import React, { useState } from "react";
import { Button, Drawer, Form, DatePicker, message, Tag } from "antd";
import {
  FilePdfOutlined,
  FileExcelOutlined,
  DeleteOutlined,
  PrinterOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { DocumentTypes } from "../../../Helpers/Constants";
import CustomTable from "../../ui/CustomTable";
import billingService from "../../../services/billing.service";
import moment from "moment";
import print from "print-js";
import {
  getInvoiceToBlobUrl,
  getTicketToBlobUrl,
  showPdfInModal,
  downloadFromData,
  downloadPDF,
} from "../../../Helpers/Functions";
import { formatLocalDate } from "../../Modification/Helpers/time";
import InvoiceCreate from "./InvoiceCreate";
const { RangePicker } = DatePicker;
let hideMessage = null;

const exportInvoicesDbf = async (filters) => {
  try {
    const data = await billingService.exportDbf(filters);
    downloadFromData(data, "application/octet-stream", "CAN26.DBF");
  } catch (error) {
    message.error("Ocurrió un error al exportar el DBF.");
  }
};
const exportInvoicesExcel = async (filters) => {
  try {
    const data = await billingService.exportExcel(filters);

    downloadFromData(
      data,
      "application/vnd.ms-excel",
      `facturacion-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  } catch (error) {
    if (error.response?.status === 403) {
      message.error("No tienes permisos para exportar este rango de fechas.");
    } else {
      message.error("Ocurrió un error al exportar el reporte.");
    }
  }
};

const exportInvoicesConcar = async (filters) => {
  try {
    const data = await billingService.exportConcar(filters);
    downloadFromData(
      data,
      "application/vnd.ms-excel",
      `concar-${new Date().toISOString().split("T")[0]}.xlsx`
    );
  } catch (error) {
    if (error.response?.status === 403) {
      message.error("Solo el superadmin puede exportar este reporte.");
    } else {
      message.error("Ocurrió un error al exportar CONCAR.");
    }
  }
};

const Invoices = () => {
    const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState("/billing/invoices");
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [form] = Form.useForm();

  const cancellInvoice = async (id) => {
    setIsLoading(true);
    try {
      await billingService.cancellInvoice(id);
      setUrl((url) => url + "?nc=" + Math.random() * 100);
    } catch (error) {
      message.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLoadingRecord = (status) => {
    if (status) {
      hideMessage = message.loading("Descargando...", 0);
    } else if (hideMessage) {
      hideMessage();
      hideMessage = null;
    }
  };

  const printInvoice = async (record) => {
    toggleLoadingRecord(true);
    const url = await getTicketToBlobUrl(record.external_invoice_id);
    print(url);
    toggleLoadingRecord(false);
  };

  const columns = [
    {
      title: "#",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: true,
      width: 100,
    },
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      align: "right",
      render: (amount) => (
        <strong>
          {new Intl.NumberFormat("es-PE", {
            style: "currency",
            currency: "PEN",
          }).format(amount)}
        </strong>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "invoice_type",
      key: "invoice_type",
      sorter: true,
      align: "right",
    },
    {
      title: "Fecha",
      dataIndex: "issue_date",
      key: "issue_date",
      sorter: true,
      render: (issue_date) =>  formatLocalDate(moment(issue_date)),
    },
    {
      title: "Cliente",
      dataIndex: "customer_name",
      key: "customer_name",
      sorter: true,
    },
    {
      title: "Documento",
      dataIndex: "customer_document_number",
      key: "customer_document_number",
      render: (value, record) =>
        DocumentTypes[record.customer_document_type] + ": " + value,
      sorter: true,
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      sorter: true,
      render: (status) =>
        status === "cancelled" ? (
          <Tag color="red">Anulado</Tag>
        ) : (
          <Tag color="blue">Emitido</Tag>
        ),
    },
    {
      title: "Acciones",
      dataIndex: "id",
      key: "edit",
      render: (id, record) => (
        <div className="flex" style={{ gap: 10 }}>
          <Button style={{ padding: 0 }} type="link" href={record.pdf_url}>
            <FilePdfOutlined />
          </Button>
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={() => printInvoice(record)}
          >
            <PrinterOutlined />
          </Button>
          <Button style={{ padding: 0 }} type="link" href={record.xml_url}>
            <FileExcelOutlined />
          </Button>
          <Button
            style={{ padding: 0 }}
            loading={isLoading}
            type="link"
            danger
            onClick={() => cancellInvoice(id)}
          >
            <DeleteOutlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="container">

       <InvoiceCreate
        open={open}
        onClose={() => setOpen(false)}
        onEmitted={(payload) => {
          setUrl((url) => url + "?nc=" + Math.random() * 100);
        }}
      />

      <CustomTable
        edit={currentId}
        title="Facturas"
        columns={columns}
        onCancel={() => setCurrentId(null)}
        url={url}
      >
        {({ filters, filterText, applyFilter, resetFilters }) => (
          <div className="flex">

             <Button type='dashed' onClick={() => setOpen(true)}>
        + Nueva Factura
      </Button>

            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setFilterVisible(true);
              }}
            >
              Filtros
            </Button>

            <Drawer
              visible={filterVisible}
              onClose={() => setFilterVisible(false)}
              title="Filtrar facturas"
              width={350}
            >
              <Form layout="vertical" form={form}>
                <Form.Item name={"range"} label="Rango de fechas">
                  <RangePicker
                    value={selectedRange}
                    ranges={{
                      Hoy: [moment(), moment()],
                      Ayer: [
                        moment().add(-1, "days").startOf("day"),
                        moment().add(-1, "days").endOf("day"),
                      ],
                      "Este mes": [
                        moment().startOf("month"),
                        moment().endOf("month"),
                      ],
                      "El mes pasado": [
                        moment().add(-1, "months").startOf("month"),
                        moment().add(-1, "months").endOf("month"),
                      ],
                    }}
                    onChange={(values) => {
                      if (values) {
                        setSelectedRange(values);
                        applyFilter({
                          from: values[0].format("YYYY-MM-DD"),
                          to: values[1].format("YYYY-MM-DD"),
                        });
                      }
                    }}
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    onClick={() => {
                      resetFilters();
                      setTimeout(() => {
                        form.resetFields();
                      }, 100);
                      setSelectedRange(null);
                    }}
                    icon={<ReloadOutlined />}
                    block
                  >
                    Resetear filtros
                  </Button>
                </Form.Item>
              </Form>
            </Drawer>

            <Button
              type="primary"
              ghost
              style={{ marginLeft: 8 }}
              onClick={() => exportInvoicesExcel({ ...filters, filterText })}
            >
              Exportar
            </Button>

            <Button
              type="primary"
              danger
              style={{ marginLeft: 8 }}
              onClick={() => exportInvoicesDbf(filters)}
            >
              Exportar DBF
            </Button>

            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              style={{ marginLeft: 8 }}
              onClick={() => exportInvoicesConcar(filters)}
            >
              Excel CONCAR
            </Button>
          </div>
        )}
      </CustomTable>
    </div>
  );
};

export default Invoices;
